// ===========================================================================
// ReHome — ErrorBoundary & UI Utilities
// Shared across all 3 apps.
// ===========================================================================

import React from 'react';
import { AlertTriangle, RefreshCw, CheckCircle2, XCircle, Info } from 'lucide-react';

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

// ─── Error Boundary ─────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isDev()) console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[60vh] flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Váratlan hiba történt</h2>
            <p className="text-gray-500 mb-2 text-sm">
              {this.state.error?.message || 'Ismeretlen hiba'}
            </p>
            <p className="text-gray-400 text-xs mb-6">
              Az alkalmazás egy váratlan hibába ütközött. Kérlek próbáld újra.
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Újrapróbálás
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Loading Skeleton ───────────────────────────────────────────────────────

export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} role="status" aria-label="Betöltés..." />
);

export const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
    <Skeleton className="aspect-[4/3] rounded-none" />
    <div className="p-4 space-y-3">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
);

export const AnimalGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }, (_, i) => (
      <CardSkeleton key={i} />
    ))}
  </div>
);

export const TableRowSkeleton: React.FC<{ cols?: number }> = ({ cols = 4 }) => (
  <tr>
    {Array.from({ length: cols }, (_, i) => (
      <td key={i} className="px-6 py-4"><Skeleton className="h-4 w-full" /></td>
    ))}
  </tr>
);

// ─── 404 Not Found Page ─────────────────────────────────────────────────────

export const NotFoundPage: React.FC<{ message?: string }> = ({ message }) => (
  <div className="min-h-[60vh] flex items-center justify-center p-8">
    <div className="max-w-md text-center">
      <div className="text-6xl font-bold text-gray-200 mb-4">404</div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Az oldal nem található</h2>
      <p className="text-gray-500 mb-6">{message || 'A keresett oldal nem létezik vagy el lett távolítva.'}</p>
      <a href="/" className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors">
        ← Vissza a főoldalra
      </a>
    </div>
  </div>
);

// ─── Toast Notification ─────────────────────────────────────────────────────

export interface ToastData {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

let toastListeners: Array<(toast: ToastData) => void> = [];
let toastId = 0;

export const toast = {
  success: (message: string) => emit({ id: `t${++toastId}`, type: 'success', message }),
  error: (message: string) => emit({ id: `t${++toastId}`, type: 'error', message }),
  info: (message: string) => emit({ id: `t${++toastId}`, type: 'info', message }),
  subscribe: (fn: (toast: ToastData) => void) => {
    toastListeners.push(fn);
    return () => { toastListeners = toastListeners.filter(l => l !== fn); };
  },
};

function emit(toast: ToastData) {
  toastListeners.forEach(fn => fn(toast));
  setTimeout(() => {
    toastListeners.forEach(fn => fn({ ...toast, id: toast.id + '-remove' } as any));
  }, 4000);
}

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  React.useEffect(() => {
    return toast.subscribe((t) => {
      if ((t as any).id?.endsWith('-remove')) {
        setToasts(prev => prev.filter(x => x.id !== (t as any).id.replace('-remove', '')));
      } else {
        setToasts(prev => [...prev, t]);
      }
    });
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 max-w-sm pointer-events-none">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`px-4 py-3 rounded-xl shadow-2xl text-sm font-semibold flex items-center gap-2 pointer-events-auto ${
            t.type === 'success' ? 'bg-emerald-600 text-white' :
            t.type === 'error' ? 'bg-red-600 text-white' :
            'bg-gray-800 text-white'
          }`}
        >
          {t.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : t.type === 'error' ? <XCircle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
          {t.message}
        </div>
      ))}
    </div>
  );
};
